import { Controller } from "@hotwired/stimulus"

// The choice-field options editor (decision-76): one text box per option.
// Enter appends the next row and focuses it, "Paste a List" turns one line
// per option into rows, ✕ removes a row. Rows left blank are dropped by the
// server, so an extra empty row is harmless.
//
// Connects to data-controller="choice-options"
export default class extends Controller {
  static targets = ["list", "row", "template", "pastePanel", "pasteInput"]

  add(event) {
    event?.preventDefault()
    this.appendRow("").focus()
  }

  // Enter in an option box: never submit the form; move to the row after
  // this one, adding it when this is the last.
  next(event) {
    if (event.isComposing) return
    event.preventDefault()

    const row = event.target.closest("[data-choice-options-target='row']")
    const following = row?.nextElementSibling
    const followingInput = following?.querySelector("input[type='text']")
    if (followingInput && followingInput.value.trim() === "") {
      followingInput.focus()
      return
    }

    const input = this.appendRow("", row)
    input.focus()
  }

  remove(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-choice-options-target='row']")?.remove()
  }

  openPaste(event) {
    event?.preventDefault()
    this.pastePanelTarget.classList.remove("hidden")
    this.pasteInputTarget.focus()
  }

  closePaste(event) {
    event?.preventDefault()
    this.pasteInputTarget.value = ""
    this.pastePanelTarget.classList.add("hidden")
  }

  // One option per line; blank lines and lines already present (any case)
  // are skipped. Empty rows are cleared first so the pasted list reads in
  // order.
  addPasted(event) {
    event?.preventDefault()
    const existing = new Set(this.labelInputs().map((input) => input.value.trim().toLowerCase()).filter(Boolean))

    this.rowTargets.forEach((row) => {
      const input = row.querySelector("input[type='text']")
      if (input && input.value.trim() === "" && this.hiddenValue(row) === "") row.remove()
    })

    this.pasteInputTarget.value.split(/\r?\n/).forEach((line) => {
      const label = line.trim()
      if (label === "" || existing.has(label.toLowerCase())) return
      existing.add(label.toLowerCase())
      this.appendRow(label)
    })

    this.closePaste()
  }

  // private

  appendRow(label, after = null) {
    const fragment = this.templateTarget.content.cloneNode(true)
    const row = fragment.firstElementChild
    const input = row.querySelector("input[type='text']")
    input.value = label

    if (after) {
      after.after(row)
    } else {
      this.listTarget.appendChild(row)
    }
    return input
  }

  labelInputs() {
    return this.rowTargets.map((row) => row.querySelector("input[type='text']")).filter(Boolean)
  }

  hiddenValue(row) {
    return row.querySelector("input[type='hidden']")?.value || ""
  }
}
